import React, { useEffect, useRef, useState } from 'react';
import TopBar from '../design/components/TopBar';
import LeaveBtn from '../design/components/LeaveBtn';
import OpponentStrip from '../design/components/OpponentStrip';
import MysteryCard from '../design/components/MysteryCard';
import PinScatter from '../design/components/PinScatter';
import BigBtn from '../design/components/BigBtn';
import { useIsDesktop } from '../hooks/useIsDesktop';

// Phases: intro (face-up card) → shining (revealed) → flipping → playing → submitted
const FLIP_START_MS = 900;
const GROW_AT_MS = FLIP_START_MS + 350;
const PLAYING_AT_MS = 1700;

const GuessScreen = ({
  cricketer = {},
  xAxis = '',
  yAxis = '',
  xRange = { min: 0, max: 100 },
  yRange = { min: 0, max: 100 },
  currentRound = 1,
  totalRounds = 5,
  players = [],
  currentPlayerId = null,
  onSubmitGuess,
  onLeave,
}) => {
  const [phase, setPhase] = useState('intro');
  const [revealed, setRevealed] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [grown, setGrown] = useState(false);
  const [guess, setGuess] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const timersRef = useRef([]);
  const isDesktop = useIsDesktop();

  // reset on new round
  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase('intro');
    setRevealed(false);
    setFlipped(false);
    setGrown(false);
    setGuess(null);
    setHasSubmitted(false);
  }, [currentRound]);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const startReveal = () => {
    if (phase !== 'intro') return;
    setRevealed(true);
    setPhase('shining');
    timersRef.current.push(setTimeout(() => { setFlipped(true); setPhase('flipping'); }, FLIP_START_MS));
    timersRef.current.push(setTimeout(() => setGrown(true), GROW_AT_MS));
    timersRef.current.push(setTimeout(() => setPhase('playing'), PLAYING_AT_MS));
  };

  const handlePlace = (pt) => {
    if (hasSubmitted) return;
    setGuess(pt);
  };

  const handleLock = () => {
    if (hasSubmitted || !guess || !onSubmitGuess) return;
    onSubmitGuess(guess);
    setHasSubmitted(true);
    setPhase('submitted');
  };

  const submittedIds = new Set(hasSubmitted && currentPlayerId ? [currentPlayerId] : []);

  // Before grow: centered portrait card. After grow: fill the entire stage (no dead space).
  const inIntro = !grown;

  if (isDesktop) {
    return (
      <div className="pen-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 24, gap: 16 }}>
        <TopBar round={currentRound} total={totalRounds} title="GUESS THE SPOT" right={<LeaveBtn onLeave={onLeave} confirm />} />

        <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16 }}>
          <div style={{ flex: '0 0 70%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <div style={inIntro ? {
              position: 'relative',
              alignSelf: 'center', margin: 'auto',
              width: 'min(360px, 80%)',
              aspectRatio: '3 / 4',
              filter: phase === 'flipping' ? 'drop-shadow(0 8px 16px #16131530)' : 'none',
              transition: 'width 420ms var(--ease-snap)',
            } : {
              position: 'relative', flex: 1, width: '100%', minHeight: 0,
            }}>
              <MysteryCard
                flipped={flipped}
                revealed={revealed}
                cricketerName={cricketer.name}
                cardNumber={`#${String(currentRound).padStart(3, '0')}`}
                onTap={startReveal}
                backChildren={
                  <PinScatter
                    embedded
                    xAxis={xAxis}
                    yAxis={yAxis}
                    xRange={xRange}
                    yRange={yRange}
                    myGuess={guess}
                    isInteractive={!hasSubmitted}
                    onPlace={handlePlace}
                    topTitle={(cricketer.name || 'DROP YOUR PIN').toUpperCase()}
                    topBadge={(cricketer.role || 'CAREER').toString().toUpperCase()}
                  />
                }
              />
            </div>
          </div>

          <div style={{
            flex: '0 0 30%',
            padding: '12px',
            background: 'var(--c-cream)',
            border: 'var(--border-ink)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-hard-sm)',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.8,
              color: 'var(--c-ink)', opacity: 0.75,
            }}>PLAYERS</div>
            {players.map((p, i) => {
              const id = typeof p === 'string' ? p : p?.id || p?.name;
              const submitted = submittedIds.has(id);
              const isMe = id === currentPlayerId;
              return (
                <div key={id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px',
                  background: isMe ? 'var(--c-yellow)' : 'var(--c-cream)',
                  border: 'var(--border-ink-sm)',
                  borderRadius: 8,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 999,
                    background: 'var(--c-cream)',
                    border: 'var(--border-ink-sm)',
                    display: 'grid', placeItems: 'center', fontSize: 14,
                    flexShrink: 0,
                  }}>{i + 1}</div>
                  <div style={{
                    flex: 1, minWidth: 0,
                    fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 0.4,
                    color: 'var(--c-ink)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{(isMe ? 'YOU' : (id || '—')).toString().toUpperCase()}</div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
                    padding: '3px 6px', borderRadius: 4,
                    background: submitted ? 'var(--c-green)' : 'var(--c-ink)',
                    color: submitted ? 'var(--c-ink)' : 'var(--c-yellow)',
                  }}>{submitted ? '✓ LOCKED' : '○ PLACING'}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {phase === 'intro' && (
            <BigBtn onClick={startReveal} primary>★ REVEAL THE BATTER</BigBtn>
          )}
          {(phase === 'shining' || phase === 'flipping') && (
            <BigBtn disabled>REVEALING…</BigBtn>
          )}
          {phase === 'playing' && !guess && (
            <BigBtn disabled>TAP CHART TO PLACE</BigBtn>
          )}
          {phase === 'playing' && guess && (
            <>
              <BigBtn onClick={() => setGuess(null)} ghost>UNDO</BigBtn>
              <BigBtn onClick={handleLock} primary>LOCK IN →</BigBtn>
            </>
          )}
          {phase === 'submitted' && (
            <BigBtn disabled>✓ LOCKED · WAITING FOR OTHERS</BigBtn>
          )}
        </div>
      </div>
    );
  }

  return (
    // position:fixed inset:0 is the only cross-browser way to match real visible viewport on mobile
    <div className="pen-screen" style={{ position: 'fixed', inset: 0, overflow: 'hidden', minHeight: 'unset', height: '100%' }}>
      <TopBar round={currentRound} total={totalRounds} title="GUESS THE SPOT" right={<LeaveBtn onLeave={onLeave} confirm />} />
      <OpponentStrip players={players} submittedIds={submittedIds} currentPlayerId={currentPlayerId} />

      {/* Stage — takes all remaining height between strip and action bar */}
      <div style={{
        position: 'relative',
        flex: 1, minHeight: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'stretch',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          padding: inIntro ? '4px 24px 16px' : '0 12px 4px',
          transition: 'padding 800ms var(--ease-snap)',
        }}>
          <div style={inIntro ? {
            /* intro/flip: centred portrait card */
            position: 'relative',
            alignSelf: 'center',
            margin: 'auto',
            width: 'min(280px, 78%)',
            aspectRatio: '3 / 4',
            filter: phase === 'flipping' ? 'drop-shadow(0 8px 16px #16131530)' : 'none',
            transition: 'width 420ms var(--ease-snap)',
          } : {
            /* playing: fill stage but cap height so plot stays landscape-ish */
            position: 'relative',
            flex: 1,
            width: '100%',
            minHeight: 0,
            /* cap at ~1:1 of available width so the chart never goes portrait */
            maxHeight: 'calc(100vw * 1.05)',
            filter: 'none',
          }}>
            <MysteryCard
              flipped={flipped}
              revealed={revealed}
              cricketerName={cricketer.name}
              cardNumber={`#${String(currentRound).padStart(3, '0')}`}
              onTap={startReveal}
              backChildren={
                <PinScatter
                  embedded
                  xAxis={xAxis}
                  yAxis={yAxis}
                  xRange={xRange}
                  yRange={yRange}
                  myGuess={guess}
                  isInteractive={!hasSubmitted}
                  onPlace={handlePlace}
                  topTitle={(cricketer.name || 'DROP YOUR PIN').toUpperCase()}
                  topBadge={(cricketer.role || 'CAREER').toString().toUpperCase()}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div style={{
        padding: '8px 14px 12px',
        display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0,
      }}>
        {phase === 'intro' && (
          <BigBtn onClick={startReveal} primary>★ REVEAL THE BATTER</BigBtn>
        )}
        {(phase === 'shining' || phase === 'flipping') && (
          <BigBtn disabled>REVEALING…</BigBtn>
        )}
        {phase === 'playing' && !guess && (
          <BigBtn disabled>TAP CHART TO PLACE</BigBtn>
        )}
        {phase === 'playing' && guess && (
          <>
            <BigBtn onClick={() => setGuess(null)} ghost>UNDO</BigBtn>
            <BigBtn onClick={handleLock} primary>LOCK IN →</BigBtn>
          </>
        )}
        {phase === 'submitted' && (
          <BigBtn disabled>✓ LOCKED · WAITING FOR OTHERS</BigBtn>
        )}
      </div>
    </div>
  );
};

export default GuessScreen;
